import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Chip,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import React from "react";

import { ACCENT_COLOR, monospaceStyle } from "../../theme";
import { ChainCard, ChainEdge, ValueChain } from "../../types/valueChain";
import EntityLink from "../Detail/EntityLink";
import { codeWords } from "../Detail/format";
import { ChainCardRef } from "../ValueChain/valueChainState";
import {
  MakeRoot,
  cardName,
  chainCards,
  isInChain,
  usePageChain,
} from "./kindChain";

// The side of the root a neighbour is on: the kinds it is built into, or the
// kinds of part stated inside it
export type NeighbourSide = "hosts" | "parts";

// One neighbour of the root with the edge between them
export interface ChainNeighbour {
  card: ChainCard;
  edge: ChainEdge;
}

// Rows a table shows; the rest are in the value chain on the root, read to
// the table's side
export const NEIGHBOURS_SHOWN = 5;

// What a table holds, in words, by side
const SIDE_WORDS: Record<NeighbourSide, { column: string; subtitle: string }> = {
  hosts: { column: "Host kind", subtitle: "kinds of host, by stated pairs" },
  parts: {
    column: "Part kind",
    subtitle: "kinds of part stated inside, by stated pairs",
  },
};

// What a cell reads where the read holds nothing
const NOTHING = "—";

/**
 * The neighbours of a chain's root on one side, joined to the root by an
 * edge, the most stated pairs first, then the most articles.
 */
export const chainNeighbours = (
  chain: ValueChain,
  side: NeighbourSide,
): ChainNeighbour[] => {
  const cards = new Map(chainCards(chain).map((card) => [card.id, card]));
  const rootId = chain.root.id;
  return chain.edges
    .filter((edge) =>
      side === "hosts" ? edge.part === rootId : edge.host === rootId,
    )
    .map((edge) => ({
      card: cards.get(side === "hosts" ? edge.host : edge.part),
      edge,
    }))
    .filter(
      (neighbour): neighbour is ChainNeighbour => neighbour.card !== undefined,
    )
    .sort(
      (a, b) =>
        b.edge.pairs - a.edge.pairs ||
        b.edge.articles - a.edge.articles ||
        cardName(a.card).localeCompare(cardName(b.card)),
    );
};

const numberCell = {
  align: "right" as const,
  sx: { ...monospaceStyle, whiteSpace: "nowrap" },
};

// The chip of a neighbour in the chain the page shows: small, outlined in
// the accent, written in the link colour
const thisChainChipSx = (theme: Theme) => {
  // Rendered without the app theme, e.g. in a test, the palette has no accent
  const accent = theme.palette.accent?.main ?? ACCENT_COLOR;
  return {
    ml: 1,
    height: 20,
    fontSize: "0.71875rem",
    verticalAlign: "middle",
    color: "link.main",
    borderColor: alpha(accent, 0.45),
    backgroundColor: alpha(accent, 0.1),
    "& .MuiChip-label": { px: 0.875 },
  };
};

interface ChainNeighboursProps {
  rows: ChainNeighbour[];
  side: NeighbourSide;
  // The table's name, for reading it out
  label: string;
  // The kind or the class the rows are next to, which the value chain opens on
  root: ChainCardRef;
  // Makes a neighbour, or the root, the root of the value chain
  onMakeRoot: MakeRoot;
}

/**
 * Kinds and classes next to the root: each name opens its view, with its
 * level, its stated pairs and the articles behind them, a mark where the
 * chain the page shows holds it, and a button that opens the chain on it.
 * The first rows only; the value chain on the root, read to the table's
 * side, shows them all.
 */
const ChainNeighbours: React.FC<ChainNeighboursProps> = ({
  rows,
  side,
  label,
  root,
  onMakeRoot,
}) => {
  const page = usePageChain();
  const words = SIDE_WORDS[side];
  const shown = rows.slice(0, NEIGHBOURS_SHOWN);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {words.subtitle}
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" aria-label={label}>
          <TableHead>
            <TableRow>
              <TableCell>{words.column}</TableCell>
              <TableCell>Level</TableCell>
              <TableCell align="right">Pairs</TableCell>
              <TableCell align="right">Articles</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map(({ card, edge }) => {
              const name = cardName(card);
              return (
                <TableRow key={card.id}>
                  <TableCell>
                    <EntityLink
                      id={card.category_id}
                      type={card.type}
                      name={name}
                    />
                    {card.type === "class" && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          typography: "caption",
                          color: "text.secondary",
                        }}
                      >
                        class
                      </Box>
                    )}
                    {isInChain(page?.chain, card) && (
                      <Chip
                        label="this chain"
                        size="small"
                        variant="outlined"
                        sx={thisChainChipSx}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {card.nature ? codeWords(card.nature) : NOTHING}
                  </TableCell>
                  <TableCell {...numberCell}>{edge.pairs}</TableCell>
                  <TableCell
                    {...numberCell}
                    sx={{ ...numberCell.sx, color: "text.secondary" }}
                  >
                    {edge.articles}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 40 }}>
                    <Tooltip title="Open the chain on it">
                      <IconButton
                        size="small"
                        aria-label={`Open the chain on ${name}`}
                        onClick={() =>
                          onMakeRoot({ type: card.type, id: card.id })
                        }
                        sx={{ color: "text.disabled" }}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {rows.length > shown.length && (
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={() => onMakeRoot(root, side)}
          sx={{ alignSelf: "flex-start", textAlign: "left" }}
        >
          {`Show all ${rows.length} · open the value chain on this ${root.type}`}
          <Box component="span" aria-hidden>
            {" →"}
          </Box>
        </Link>
      )}
    </Box>
  );
};

export default ChainNeighbours;
